import type { CalendarStripItem, CardDto, DayMode, TodayResponse } from '@konvert/shared';
import { formatDateWithWeekdayRu } from '@konvert/domain';
import { LoadBar } from '../components/Load.js';
import { CardTile } from '../components/CardTile.js';
import { Banner } from '../components/Droppables.js';
import { NewCardForm, type NewCardPayload } from '../components/NewCardForm.js';
import { t } from '../lib/i18n.js';

const MODES: DayMode[] = ['normal', 'hard', 'off'];

export function TodayScreen({
  data,
  strip,
  timezone,
  threshold,
  onOpenCard,
  onCreate,
  onSetMode,
  onOpenTriage,
}: {
  data: TodayResponse;
  strip: CalendarStripItem[];
  timezone: string;
  threshold: number;
  onOpenCard: (card: CardDto) => void;
  onCreate: (payload: NewCardPayload) => void;
  onSetMode: (mode: DayMode) => void;
  onOpenTriage: () => void;
}): JSX.Element {
  const conflicting = new Set(data.conflicts.flatMap((c) => [c.a_card_id, c.b_card_id]));
  void strip;

  return (
    <div className="space-y-4">
      <section className="envelope p-4">
        <header className="mb-3 flex items-baseline justify-between">
          <div>
            <h1 className="text-lg text-ink">{t('day.title')}</h1>
            <p className="font-hand text-xl text-ink-soft">{formatDateWithWeekdayRu(data.date)}</p>
          </div>
          <div className="flex gap-1">
            {MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onSetMode(mode)}
                className={`rounded-full px-2 py-1 text-xs ${
                  data.mode === mode
                    ? 'bg-[var(--ink)] text-[var(--paper)]'
                    : 'border border-[var(--line)] text-ink-soft'
                }`}
              >
                {t(`day.mode.${mode}` as 'day.mode.normal')}
              </button>
            ))}
          </div>
        </header>

        <LoadBar load={data.load} />

        <div className="mt-3 space-y-2">
          <Banner text={t('day.carryover')} count={data.carryover_count} onClick={onOpenTriage} />
          <Banner text={t('day.proposals')} count={data.pending_proposals_count} />
        </div>

        <div className="mt-4 space-y-2">
          {data.cards.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-soft">{t('day.empty')}</p>
          ) : (
            data.cards.map((card) => (
              <CardTile
                key={card.id}
                card={card}
                onOpen={onOpenCard}
                conflicting={conflicting.has(card.id)}
                threshold={threshold}
              />
            ))
          )}
        </div>

        <p className="mt-4 text-xs text-ink-soft">
          {t('day.done')}: {data.done_today_count}
        </p>
      </section>

      <NewCardForm date={data.date} timezone={timezone} onCreate={onCreate} />
    </div>
  );
}
